import {
  Component,
  Input,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { MatTable } from "@angular/material/table";
import {
  CompanyNavPageDataService,
  NavRowData,
  NAV_PAGE,
} from "../company-nav-page-data.service";
import {
  VAIconRegistryService,
  plusCircle,
  minusCircle,
  plus,
} from "@va/core-library/icons";
import { Observable, of } from "rxjs";
import { mergeMap } from "rxjs/operators";
import { formatCell } from "src/app/utils/common";
import { AppService } from "src/app/app.service";
import { Store } from "@ngrx/store";
import { AppState } from "src/app/store/state/app.state";
import {
  DeleteDpParams,
  SetDpParams,
} from "src/app/store/actions/global-subheader.actions";

@Component({
  selector: "app-company-nav-grid",
  templateUrl: "./company-nav-grid.component.html",
  styleUrls: ["./company-nav-grid.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class CompanyNavGridComponent {
  displayedColumns: string[] = [];
  displayedSourceColumns: string[] = [];
  columnList = [];
  sourceColumnsList = [];
  displayedSourceColumnsHeader = [];
  tableReady: boolean = false;
  dataSource = [];
  parameterIdsMapping = {};
  @Input() metaInfo;
  revisionVal = "Amount";
  expandableSourceIds = [11111, 10001, 10002];
  sourceInfo;
  companyDefaultSource;
  dataSettingsApplied: boolean = false;
  assets = window.assetUrl;
  sourceLevelNotes = {};
  dataPointLevelNotes = {};
  selectedRid = "";
  defaultLoadSelect = "";
  defaultLoadSelectSourceIds = [11111, 10001, 10002];

  @Input() currentTicker;
  @Input() dataSettings;
  @Input() metaLoading: boolean = true;
  hasMetaError: boolean = false;
  errMsg = "Something went wrong!";
  displayUnitMag = "";

  @ViewChild(MatTable) table: MatTable<NavRowData>;

  constructor(
    private dataService: CompanyNavPageDataService,
    private store: Store<AppState>,
    private iconRegistry: VAIconRegistryService,
    private appService: AppService,
  ) {
    this.iconRegistry.registerIcons([plusCircle, minusCircle, plus]);
  }
  ngOnInit() {
    this.tableReady = false;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.currentTicker && changes.currentTicker.currentValue !== "") {
      this.currentTicker = changes.currentTicker.currentValue;
    } else if (changes.metaInfo && changes.metaInfo.currentValue) {
      this.hasMetaError = false;
      this.companyDefaultSource = this.companyDefaultSource
        ? this.companyDefaultSource
        : { ...this.metaInfo.default_source };
      if (
        changes.metaInfo.currentValue.user_settings &&
        changes.metaInfo.currentValue.user_settings.keyChanged &&
        !this.dataSettingsApplied
      ) {
        this.dataSettings = {
          ...changes.metaInfo.currentValue.user_settings,
        };
      }
      this.dataSettingsApplied =
        this.dataSettingsApplied || this.dataSettings.keyChanged === "currency";
      if (this.dataSettings.customCC && this.dataSettings.customCC.length > 0) {
        this.metaInfo.default_source = {
          id: 10002,
          name: "Custom",
        };
      } else {
        this.metaInfo.default_source = { ...this.companyDefaultSource };
      }
      if (
        changes.metaInfo.currentValue.sources_info &&
        changes.metaInfo.currentValue.sources_info.length === 0
      ) {
        this.expandableSourceIds = [];
      } else {
        this.expandableSourceIds = [11111, 10001, 10002];
      }
      this.getMetaInfo(changes.metaInfo.currentValue, this.dataSettingsApplied);
    } else if (
      changes.dataSettings &&
      changes.dataSettings.currentValue &&
      changes.dataSettings.currentValue.keyChanged !== ""
    ) {
      // data setting changed, call grid info
      this.dataSettings = { ...changes.dataSettings.currentValue };
      if (
        this.dataSettings.keyChanged !== "highLow" &&
        this.dataSettings.keyChanged !== "currency"
      ) {
        this.tableReady = false;
        this.dataSource = [];
        this.dataSettingsApplied = true;
        if (this.dataSettings.customCC.length > 0) {
          this.metaInfo.default_source = {
            id: 10002,
            name: "Custom",
          };
        } else {
          this.metaInfo.default_source = { ...this.companyDefaultSource };
        }
        this.getMetaInfo(this.metaInfo, true);
      }
    }
  }

  getColumnDisplayUnit(colUnit, colMagnitude) {
    // unit values = % , amount, other
    // magnitude = abs, other
    // currency = same as base or set or different
    let displayVal = "";
    const currencySymbol =
      this.metaInfo.user_settings && this.metaInfo.user_settings.currency_symbol
        ? this.metaInfo.user_settings.currency_symbol
        : this.metaInfo.company_level_currency_symbol;
    if (colUnit !== "%" && colUnit !== "Amount") {
      // check for magnitude
      if (colMagnitude === "Abs") {
        displayVal = colUnit;
      } else {
        displayVal = colMagnitude + colUnit;
      }
      return displayVal;
    }

    if (colUnit === "Amount") {
      const defaultMag =
        this.metaInfo.user_settings && this.metaInfo.user_settings.magnitude
          ? this.metaInfo.user_settings.magnitude
          : this.metaInfo.company_level_magnitude;
      // check for magnitude
      if (colMagnitude === "Abs") {
        displayVal = currencySymbol;
      } else if (colMagnitude !== defaultMag) {
        displayVal = currencySymbol + colMagnitude;
      }
    }

    return displayVal;
  }
  getMetaInfo(data, dataSettingsCall?) {
    this.metaInfo = data;
    this.metaLoading = false;
    if (this.metaInfo.status === "error") {
      this.hasMetaError = true;
      return;
    }
    if (this.metaInfo.user_settings && this.metaInfo.user_settings.currency) {
      this.displayUnitMag =
        this.metaInfo.user_settings.currency_symbol +
        this.metaInfo.user_settings.magnitude;
      this.dataSettings.currency = this.metaInfo.user_settings.currency;
    } else {
      this.displayUnitMag =
        this.metaInfo.company_level_currency_symbol +
        this.metaInfo.company_level_magnitude;
      this.dataSettings.currency = "";
    }
    this.defaultLoadSelect = this.metaInfo.default_select;
    this.columnList = data.column_order.map((val) => {
      let sourceList = [];
      if (this.dataSettings.customCC.length > 0) {
        sourceList = [data.default_source.id, ...this.dataSettings.customCC];
      } else {
        sourceList = [
          data.default_source.id,
          ...data.columns_info[val].source_list,
        ];
      }
      return {
        name: data.columns_info[val].name,
        colId: val,
        sourceId: data.default_source.id,
        sourceName: data.default_source.name,
        sourceColId: `${val}-${data.default_source.id}`,
        sourceList: sourceList,
        hasNote: false,
        noteText: "",
        colSpan: 1,
        displayUnitMagnitude: this.getColumnDisplayUnit(
          data.columns_info[val].unit,
          data.columns_info[val].magnitude,
        ),
      };
    });
    this.sourceColumnsList = data.column_order.map((val) => {
      return {
        name: data.columns_info[val].name,
        colId: val,
        sourceId: data.default_source.id,
        sourceName: data.default_source.name,
        sourceColId: `${val}-${data.default_source.id}`,

        analystInfo: {
          documentId: 0,
        },
        expanded: false,
      };
    });
    const cols: Array<any> = this.columnList.map((c) => c.colId.toString());
    this.displayedColumns = ["name", ...cols];
    const sourceCols: Array<any> = this.sourceColumnsList.map(
      (c) => c.sourceColId,
    );
    this.displayedSourceColumnsHeader = [...sourceCols];
    this.displayedSourceColumns = ["name", ...sourceCols];
    this.dataSource = this.dataService.convertRowData(data, this.columnList);
    if (dataSettingsCall) {
      this.dataCallForSettings();
    } else {
      this.getNotes();
    }
    this.tableReady = true;
  }

  getNotes() {
    this.dataService
      .getNotes(this.currentTicker, this.metaInfo)
      .subscribe((resp: any) => {
        if (resp.data.source_level) {
          this.sourceLevelNotes = resp.data.source_level;
        }
        if (resp.data.data_point_level) {
          this.dataPointLevelNotes = resp.data.data_point_level;
        }
        this.defaultDataCalls();
      });
  }

  defaultDataCalls() {
    const limit = 20;
    const allLineIds = this.dataSource
      .filter((val) => {
        if (!val.lineInfo.header) return val;
        return false;
      })
      .map((v) => v.lineInfo.ctag);
    const sourceList = [this.metaInfo.default_source.id];
    let startIndex = 0;
    const currentColIds = [...this.metaInfo.column_order];

    while (startIndex < allLineIds.length) {
      const currentLineIds = allLineIds.slice(startIndex, startIndex + limit);
      const paramIds = [];
      //repeat with lines or sources
      const chain: Observable<any> = this.dataService.getParameterIds(
        this.currentTicker,
        currentColIds,
        currentLineIds,
      );

      chain
        .pipe(
          mergeMap((apiResp) => {
            if (apiResp.status === "error") {
              return of({
                status: "error",
              });
            }
            apiResp = apiResp.data.cells_info;
            currentColIds.forEach((source) => {
              if (apiResp[source]) {
                this.parameterIdsMapping[source] = {
                  ...this.parameterIdsMapping[source],
                  ...apiResp[source],
                };
                currentLineIds.forEach((lineId) => {
                  if (apiResp[source][lineId]) {
                    paramIds.push(apiResp[source][lineId]);
                  }
                });
              }
            });
            if (paramIds.length === 0) {
              return of({
                status: "error",
              });
            }
            const dataSettings =
              this.metaInfo.user_settings.keyChanged !== ""
                ? this.metaInfo.user_settings
                : "";

            // execute second Call passing results from first call
            return this.dataService.getRowsData(
              this.currentTicker,
              sourceList,
              paramIds,
              this.metaInfo,
              dataSettings,
            );
          }),
        )
        .subscribe((results: any) => {
          this.setData(results, currentLineIds, sourceList);
        });
      startIndex = startIndex + limit;
    }

    this.getAnalystInfo();
  }
  getAnalystInfo() {
    //get analyst info after data calls
    //https://webapp2-uat-insights.visiblealpha.com/nav_page/get_document_analyst
    const docIds = [];
    this.sourceInfo = {};
    this.metaInfo.sources_info.forEach((element) => {
      let docId = element.document_id;
      if (this.metaInfo.sourceDocMapping[element.sid]) {
        docId =
          this.metaInfo.sourceDocMapping[element.sid].doc_id ||
          this.metaInfo.sourceDocMapping[element.sid].document_id;
      }
      this.sourceInfo[element.sid] = {
        documentId: docId,
        sourceName: element.source_name,
        analystName: "",
        analystEmail: "",
        analystId: "",
        modelDate: "",
        excluded: element.excluded,
      };
      docIds.push(docId);
    });
    if (docIds.length > 0) {
      this.dataService.getAnalystInfo(docIds).subscribe((resp) => {
        for (const key in this.sourceInfo) {
          const element = this.sourceInfo[key];
          if (resp[element.documentId]) {
            element.analystName = resp[element.documentId].name;
            element.analystId = resp[element.documentId].id;
            element.analystEmail = resp[element.documentId].email;
            element.modelDate = resp[element.documentId].model_date
              ? resp[element.documentId].model_date
              : "";
            element.hasNote = resp[element.documentId].note !== "";
            if (element.hasNote) {
              element.noteText = resp[element.documentId].note;
            }
          }
        }
      });
    }
  }
  setData(apiResults, currentLineIds, sourceList, columnId?) {
    const currentRows = this.dataSource.filter((row) =>
      currentLineIds.includes(row.lineInfo.ctag),
    );
    if (apiResults.status !== "error") {
      currentRows.forEach((row) => {
        const ctag = row.lineInfo.ctag;
        let columns = {};
        if (columnId) {
          columns[columnId] = row.navCols[columnId];
        } else {
          columns = row.navCols;
        }
        if (!row.lineInfo.header) {
          for (const colId in columns) {
            const element = row.navCols[colId];
            const precision = this.metaInfo.columns_info[colId].precision;
            element.parameterId = this.parameterIdsMapping[colId][ctag];

            const dataObj = apiResults["data"][element.parameterId];
            if (dataObj) {
              sourceList.forEach((sourceId) => {
                const cellData = element.data[sourceId];
                const notesObj = {
                  hasNote: false,
                  noteText: "",
                };
                if (
                  this.dataPointLevelNotes[element.parameterId] &&
                  this.dataPointLevelNotes[element.parameterId][sourceId]
                ) {
                  notesObj.hasNote = true;
                  notesObj.noteText =
                    this.dataPointLevelNotes[element.parameterId][
                      sourceId
                    ].notes;
                }
                if (dataObj[sourceId]) {
                  const currencySymbol = dataObj[sourceId].currency_symbol
                    ? `${dataObj[sourceId].currency_symbol} `
                    : "";
                  cellData.val = this.formatCellValue(
                    dataObj[sourceId].unit,
                    dataObj[sourceId].final_value,
                    precision,
                    currencySymbol,
                  );
                  cellData.isNegative = dataObj[sourceId].final_value < 0;
                  cellData.unit = dataObj[sourceId].unit;
                  cellData.high =
                    dataObj[sourceId].high && !dataObj[sourceId].low;
                  cellData.low =
                    dataObj[sourceId].low && !dataObj[sourceId].high;
                  cellData.hasNote = notesObj.hasNote;
                  cellData.noteText = notesObj.noteText;
                  cellData.rid = dataObj[sourceId].rid;
                  cellData.sourceID = sourceId;
                  cellData.colId = parseInt(colId);
                } else {
                  cellData.val = "-";
                }
                cellData.isLoading = false;
              });
            } else {
              sourceList.forEach((sourceId) => {
                const cellData = element.data[sourceId];
                cellData.val = "-";
                cellData.isLoading = false;
              });
            }
          }
        }
      });
    } else {
      currentRows.forEach((row) => {
        let columns = {};
        if (columnId) {
          columns[columnId] = row.navCols[columnId];
        } else {
          columns = row.navCols;
        }
        for (const colId in columns) {
          const element = row.navCols[colId];
          element.parameterId =
            this.parameterIdsMapping[colId][row.lineInfo.ctag];
          sourceList.forEach((sourceId) => {
            const cellData = element.data[sourceId];
            cellData.val = "-";
            cellData.isLoading = false;
          });
        }
      });
    }
    if (
      this.defaultLoadSelect &&
      this.defaultLoadSelectSourceIds.includes(sourceList[0]) &&
      this.dataSource[1]["navCols"][this.defaultLoadSelect]["data"][
        sourceList[0]
      ]["val"] !== "-"
    ) {
      this.onCellClick(
        this.dataSource[1]["navCols"][this.defaultLoadSelect]["data"][
          sourceList[0]
        ],
      );
    }
  }
  formatCellValue(unit, value, precision, currency) {
    const newVal = formatCell(
      value,
      currency,
      unit,
      this.dataSettings.view_by[0],
      precision,
    );
    return newVal;
  }
  makeSourceDataCalls(column, sourceList) {
    const limit = 8;
    const allLineIds = this.dataSource
      .filter((val) => {
        if (!val.lineInfo.header) return val;
        return false;
      })
      .map((v) => v.lineInfo.ctag);
    let startIndex = 0;

    while (startIndex < allLineIds.length) {
      const currentLineIds = allLineIds.slice(startIndex, startIndex + limit);
      const paramIds = [];
      //repeat with lines or sources
      currentLineIds.forEach((lineId) => {
        if (this.parameterIdsMapping[column.colId][lineId]) {
          paramIds.push(this.parameterIdsMapping[column.colId][lineId]);
        }
      });
      if (paramIds.length === 0) {
        this.setData(
          { status: "error" },
          currentLineIds,
          sourceList,
          column.colId,
        );
      } else {
        this.dataService
          .getRowsData(
            this.currentTicker,
            sourceList,
            paramIds,
            this.metaInfo,
            this.dataSettings,
          )
          .subscribe((results: any) => {
            this.setData(results, currentLineIds, sourceList, column.colId);
          });
      }
      startIndex = startIndex + limit;
    }
  }
  showSources(column) {
    //add sources to table
    let sourceArr = [];
    this.columnList.forEach((col, i) => {
      if (col.colId === column.colId) {
        sourceArr = this.columnList[i].sourceList
          .map((val) => {
            if (val === this.metaInfo.default_source.id) {
              return null;
            }

            const sourceCol = {
              name: this.metaInfo.columns_info[column.colId].name,
              colId: column.colId,
              sourceId: val,
              sourceName: this.sourceInfo[val].sourceName,
              sourceColId: `${column.colId}-${val}`,
              analystInfo: {},
              expanded: false,
              hasNote:
                this.sourceLevelNotes[val] && this.sourceLevelNotes[val].notes
                  ? true
                  : false,
              noteText:
                this.sourceLevelNotes[val] && this.sourceLevelNotes[val].notes
                  ? this.sourceLevelNotes[val].notes
                  : "",
              excluded: this.sourceInfo[val].excluded,
            };
            return sourceCol;
          })
          .filter((value) => value !== null);

        col.colSpan = sourceArr.length + 1;
      }
    });

    let index = this.sourceColumnsList.findIndex((col, indx) => {
      if (col.sourceColId === column.sourceColId) {
        col.expanded = true;
        return indx === 0 ? -2 : indx;
      }
      return false;
    });
    index = index === -2 ? 0 : index;

    this.sourceColumnsList.splice(index + 1, 0, ...sourceArr);
    const cols = sourceArr.map((c) => c.sourceColId.toString());
    this.displayedSourceColumns.splice(index + 1, 0, ...cols);
    this.displayedSourceColumnsHeader.splice(index, 0, ...cols);

    this.makeSourceDataCalls(
      column,
      sourceArr.map((val) => val.sourceId),
    );
    this.logColumnExpansion(this.metaInfo.columns_info[column.colId].name);
  }
  hideSources(column) {
    let index = this.sourceColumnsList.findIndex((col, indx) => {
      if (col.sourceColId === column.sourceColId) {
        col.expanded = false;
        return indx === 0 ? -2 : indx;
      }
      return false;
    });
    index = index === -2 ? 0 : index;
    let noOfSources = 1;
    this.columnList.forEach((col) => {
      if (col.colId === column.colId) {
        col.colSpan = 1;
        noOfSources = col.sourceList.length - 1; // -1 for consensus
      }
    });

    this.sourceColumnsList.splice(index + 1, noOfSources);
    this.displayedSourceColumns.splice(index + 1, noOfSources);
    this.displayedSourceColumnsHeader.splice(index, noOfSources);
  }

  dataCallForSettings() {
    if (this.metaInfo.source_updated) {
      this.dataService
        .getNotes(this.currentTicker, this.metaInfo)
        .subscribe((resp: any) => {
          if (resp.data.source_level) {
            this.sourceLevelNotes = resp.data.source_level;
          }
          if (resp.data.data_point_level) {
            this.dataPointLevelNotes = resp.data.data_point_level;
          }
        });
    }
    let dataCallNeeded = false;
    let otherFilterChange = false;
    const makeDataCall = this.metaInfo.makeDataCall ? true : false;
    if (
      this.metaInfo.updatedKey === "customCC" ||
      this.metaInfo.updatedKey === "highLow"
    ) {
      otherFilterChange = true;
    }
    if (this.metaInfo.prevViewBy === "revision") {
      if (
        this.metaInfo.view_by === "values" &&
        this.metaInfo.rev_date !== "today"
      ) {
        dataCallNeeded = true;
      }
    } else if (this.metaInfo.prevViewBy === "values") {
      if (this.metaInfo.prevRevDate !== "today") {
        dataCallNeeded = true;
      } else {
        if (
          this.metaInfo.view_by === "values" &&
          this.metaInfo.rev_date !== "today"
        ) {
          dataCallNeeded = true;
        }
      }
    }
    if (otherFilterChange || dataCallNeeded === makeDataCall) {
      const limit = 20;
      const allLineIds = this.dataSource
        .filter((val) => {
          if (!val.lineInfo.header) return val;
          return false;
        })
        .map((v) => v.lineInfo.ctag);
      const sourceList = [this.metaInfo.default_source.id];
      let startIndex = 0;
      const currentColIds = [...this.metaInfo.column_order];
      while (startIndex < allLineIds.length) {
        const currentLineIds = allLineIds.slice(startIndex, startIndex + limit);
        const paramIds = [];
        currentColIds.forEach((colId) => {
          currentLineIds.forEach((lineId) => {
            if (this.parameterIdsMapping[colId][lineId]) {
              paramIds.push(this.parameterIdsMapping[colId][lineId]);
            }
          });
        });
        this.dataService
          .getRowsData(
            this.currentTicker,
            sourceList,
            paramIds,
            this.metaInfo,
            this.dataSettings,
          )
          .subscribe((results: any) => {
            this.setData(results, currentLineIds, sourceList);
          });
        startIndex = startIndex + limit;
      }
      this.updateSourceInfoDocIdFromMeta();
      this.getAnalystInfo();
    }
  }

  logColumnExpansion(colName) {
    const feature = `${colName
      .toUpperCase()
      .split(" ")
      .join("_")}_COLUMN_EXPAND`;
    this.appService.logUserAction(
      {},
      this.currentTicker,
      "",
      feature,
      {},
      "",
      NAV_PAGE,
    );
  }

  updateSourceInfoDocIdFromMeta() {
    this.metaInfo.sources_info.forEach((element) => {
      let docId = element.document_id;
      if (this.metaInfo.sourceDocMapping[element.sid]) {
        docId =
          this.metaInfo.sourceDocMapping[element.sid].doc_id ||
          this.metaInfo.sourceDocMapping[element.sid].document_id;
      }
      this.sourceInfo[element.sid] = {
        ...this.sourceInfo[element.sid],
        documentId: docId,
      };
    });
  }

  onCellClick(cellInfo) {
    if (
      cellInfo &&
      cellInfo.val &&
      cellInfo.val !== undefined &&
      cellInfo.val !== null &&
      cellInfo.val !== "-"
    ) {
      this.selectedRid = cellInfo.rid;
      const source =
        parseInt(cellInfo.sourceID) === 10001
          ? "consensus"
          : parseInt(cellInfo.sourceID) === 10002
            ? "custom"
            : parseInt(cellInfo.sourceID) === 11111
              ? "consensus+"
              : this.sourceInfo[cellInfo.sourceID].sourceName.toLowerCase();
      const today = new Date();
      const mm = today.getMonth() + 1;
      const dd = today.getDate();
      const yyyy = today.getFullYear();
      const date = `${mm}/${dd}/${yyyy}`;
      const dpParams = {
        dp: `${cellInfo.rid}|${date}`, //"16151916582|09/02/2020",
        ticker: this.currentTicker,
        growthType: "Analytics",
        shouldLog: true,
        source: source,
        isNav: true,
        cut: "NAV",
        requested_depth_level: 4,
      };

      if (dpParams["source"] === "custom") {
        const included = [];
        const excluded = [];
        const customSourceDocDict = {};
        Object.keys(this.sourceInfo).forEach((id) => {
          this.dataSettings.customCC.includes(parseInt(id))
            ? included.push(parseInt(id))
            : excluded.push(parseInt(id));
        });
        dpParams["incl_sources"] = included;
        dpParams["excl_sources"] = excluded;
        included.forEach((source) => {
          customSourceDocDict[source] = this.sourceInfo[source].documentId;
        });
        dpParams["source_doc_mapping"] = JSON.stringify(customSourceDocDict);
      } else if (
        dpParams["source"] === "consensus" ||
        dpParams["source"] === "consensus+"
      ) {
        const sourceDocDict = {};
        Object.keys(this.metaInfo.sourceDocMapping).forEach((id) => {
          sourceDocDict[id] = this.metaInfo.sourceDocMapping[id].document_id;
        });
        dpParams["source_doc_mapping"] = JSON.stringify(sourceDocDict);
      }

      if (this.dataSettings.currency !== "") {
        dpParams["currency"] = this.dataSettings.currency;
      }

      this.store.dispatch(new SetDpParams(dpParams));
    } else {
      //there is no data
      this.selectedRid = "";
      this.store.dispatch(new DeleteDpParams());
    }
  }
}
